import { FC, useMemo } from 'react';
import { Col, Row, Tooltip } from 'antd';
import { beautifyTheLowHighObject, floorKeep, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import styles from './index.module.less';
import cs from 'classnames';
import { QuestionCircleOutlined } from '@ant-design/icons';
import IconFont from '@/common/components/Base/IconFont';

interface InfoProp {
  title: string;
  value?: string | number | null | undefined; // 统计数据
  unit?: string; // 单位，value有值才展示
  tag?: string; // 标签信息
  help?: string; // 说明注释
}

/** 环比/同比指标渲染 */
const RateItem: FC<{ label: string; value: number | undefined }> = ({ label, value }) => {
  if (value === undefined || value === null) return null;
  // 下降：value < 0；上升或0：其余情况
  const isDown = value < 0;
  const color = isDown ? '#02C681' : '#F23030';
  const absVal = beautifyTheLowHighObject(1, Math.abs(value), 1).val;
  return (
    <span className={styles.rateItem} style={{ color }}>
      <span>{label}{absVal}%</span>
      <IconFont
        iconHref='pc-common-icon-ic_shangsheng'
        style={{ fontSize: 12, color, transform: isDown ? 'rotate(180deg)' : 'none', display: 'inline-block' }}
      />
    </span>
  );
};

/**
 * @Description 一个统计项
 */
const StatisticsItem: FC<{
  itemData: InfoProp,
}> = ({
  itemData,
}) => {
  return (
    <div className={styles.infoItem}>
      <div className={styles.valueRow}>
        <div className={cs(styles.value, 'fs-16 c-222')}>{isNotEmpty(itemData.value) ? itemData.value : '-'}</div>
        {itemData.unit && isNotEmpty(itemData.value) ? <div className='fs-12 c-222 ml-2'>{itemData.unit}</div> : <></>}
        {
          itemData.help && (
            <Tooltip
              title={itemData.help}
              placement='bottom'
              color='#fff'
              overlayInnerStyle={{
                width: '240px',
                boxSizing: 'border-box',
                borderRadius: '4px',
                color: 'rgba(0, 0, 0, 0.85)',
                padding: '10px',
                fontSize: '12px'
              }}
            >
              <>
                <QuestionCircleOutlined className='fs-12 c-666 ml-2'/>
              </>
            </Tooltip>
          )
        }
      </div>
      <div className={styles.titleRow}>
        <div className={cs(styles.title, 'fs-12 c-666')}>{itemData.title}</div>
        {itemData.tag ? <div className={cs(styles.tag, 'ml-2')}>{itemData.tag}</div> : <></>}
      </div>
    </div>
  );
};

/** 月日均客流卡片（含环比/同比，标题行右侧展示） */
const MallFlowItem: FC<{ flowObj: { val: string; unit: string }; chainRate: number; yoyRate: number }> = ({ flowObj, chainRate, yoyRate }) => {
  return (
    <div className={styles.infoItem}>
      <div className={styles.valueRow}>
        <div className={cs(styles.value, 'fs-16 c-222')}>{flowObj.val}</div>
        {flowObj.unit ? <div className='fs-12 c-222 ml-2'>{flowObj.unit}</div> : <></>}
      </div>
      <div className={styles.titleRow}>
        <div className={cs(styles.title, 'fs-12 c-666')}>月日均客流</div>
        <div className={styles.rateRow}>
          <RateItem label='环比' value={chainRate} />
          <RateItem label='同比' value={yoyRate} />
        </div>
      </div>
    </div>
  );
};

/**
 * @Description 大圈统计数据
 */
const Statistics: FC<any> = ({
  detail,
  overViewData,
  placeData,
  className = '',
}) => {
  const methods = useMethods({
    /** 人口/客流类数值格式化，无值返回占位符 */
    getValObj(num, opts = { low: '人', high: '万人', top: '亿人' }) {
      if (!isNotEmpty(num)) return { val: '-', unit: '' };
      return beautifyTheLowHighObject(1, num, 1, opts);
    },
  });

  // 月日均客流
  const mallPassFlowObj = useMemo(() => methods.getValObj(overViewData?.mallPassFlow, { low: '人次', high: '万人次', top: '亿人次' }), [overViewData?.mallPassFlow]);
  // 常驻人口
  const residentPopObj = useMemo(() => methods.getValObj(overViewData?.residentPopulation3000m), [overViewData?.residentPopulation3000m]);
  // 居住人口
  const populationObj = useMemo(() => methods.getValObj(overViewData?.population3km), [overViewData?.population3km]);
  // 本品牌门店数
  const ownBrandObj = useMemo(() => methods.getValObj(placeData?.ownBrandShopNum, { low: '个', high: '万个', top: '亿个' }), [placeData?.ownBrandShopNum]);
  // 竞品门店
  const competitorObj = useMemo(() => methods.getValObj(placeData?.competitorShopNum, { low: '个', high: '万个', top: '亿个' }), [placeData?.competitorShopNum]);
  // 活跃用户
  const activeUserObj = useMemo(() => methods.getValObj(overViewData?.activeUserCount), [overViewData?.activeUserCount]);

  // 环比、同比
  const chainRate = overViewData?.avgDayUvMomRate;
  const yoyRate = overViewData?.avgDayUvYoyRate;

  return (
    <div className={cs(className, styles.statistics)}>
      {/* 行1：月日均客流(span=12，含环比/同比) + 常驻人口(6) + 居住人口(6) */}
      <Row gutter={8}>
        <Col span={12}>
          <MallFlowItem flowObj={mallPassFlowObj} chainRate={chainRate} yoyRate={yoyRate} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '常驻人口', value: residentPopObj.val, unit: residentPopObj.unit }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '居住人口', value: populationObj.val, unit: populationObj.unit }} />
        </Col>
      </Row>

      {/* 行2：学习机销量 + 电话手表销量 + 总销量 + 本品牌门店 */}
      <Row gutter={8} className='mt-8'>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '学习机销量', value: detail.category2Sales, unit: '台' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '电话手表销量', value: detail.category1Sales, unit: '台' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '总销量', value: detail.totalSales, unit: '台' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '本品牌门店', value: ownBrandObj.val, unit: ownBrandObj.unit }} />
        </Col>
      </Row>

      {/* 行3：学习机营业额 + 电话手表营业额 + 总营业额 + 竞品门店 */}
      <Row gutter={8} className='mt-8'>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '学习机营业额', value: isNotEmpty(detail.category2Revenue) ? floorKeep(detail.category2Revenue, 10000, 4, 2) : null, unit: '万元' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '电话手表营业额', value: isNotEmpty(detail.category1Revenue) ? floorKeep(detail.category1Revenue, 10000, 4, 2) : null, unit: '万元' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '总营业额', value: isNotEmpty(detail.totalRevenue) ? floorKeep(detail.totalRevenue, 10000, 4, 2) : null, unit: '万元' }} />
        </Col>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '竞品门店', value: competitorObj.val, unit: competitorObj.unit }} />
        </Col>
      </Row>

      {/* 行4：活跃用户 + 适龄人口 + 商圈年购买人数估算（第4格留空） */}
      <Row gutter={8} className='mt-8'>
        <Col span={6}>
          <StatisticsItem itemData={{ title: '活跃用户', value: activeUserObj.val, unit: activeUserObj.unit }} />
        </Col>
      </Row>
    </div>
  );
};

export default Statistics;
